import React, { useState, useEffect } from "react";
import { X, Cookie, Shield, BarChart3, Target } from "lucide-react";
import { Button } from "./components/ui/button";
import "./CookieConsent.css";

const COOKIE_CONSENT_KEY = "isoliamo_cookie_consent";
const COOKIE_PREFERENCES_KEY = "isoliamo_cookie_preferences";

// Cookie categories
const COOKIE_CATEGORIES = {
  necessary: {
    id: "necessary",
    name: "Necessari",
    description: "Cookie essenziali per il funzionamento del sito. Non possono essere disattivati.",
    icon: Shield,
    required: true,
    cookies: ["session", "csrf_token", "cookie_consent"]
  },
  analytics: {
    id: "analytics",
    name: "Analitici",
    description: "Ci aiutano a capire come i visitatori interagiscono con il sito (Google Analytics).",
    icon: BarChart3,
    required: false,
    cookies: ["_ga", "_gid", "_gat"]
  },
  marketing: {
    id: "marketing",
    name: "Marketing",
    description: "Utilizzati per mostrarti pubblicità pertinenti su Meta (Facebook/Instagram) e Google Ads.",
    icon: Target,
    required: false,
    cookies: ["_fbp", "_fbc", "_gcl_au", "_gcl_aw"]
  }
};

function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    } else {
      // Load saved preferences and initialize scripts
      const savedPreferences = JSON.parse(localStorage.getItem(COOKIE_PREFERENCES_KEY) || "{}");
      setPreferences(prev => ({ ...prev, ...savedPreferences }));
      initializeScripts(savedPreferences);
    }
  }, []);

  const initializeScripts = (prefs) => {
    // Initialize Google Analytics
    if (prefs.analytics) {
      initGoogleAnalytics();
    }
    
    // Initialize Meta Pixel and Google Ads
    if (prefs.marketing) {
      initMetaPixel();
      initGoogleAds();
    }
  };

  const initGoogleAnalytics = () => {
    // Google Analytics 4 (GA4)
    // Replace G-XXXXXXXXXX with your actual GA4 Measurement ID
    if (!window.gtag) {
      const script = document.createElement("script");
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX";
      script.async = true;
      document.head.appendChild(script);
      
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
      window.gtag("config", "G-XXXXXXXXXX", {
        anonymize_ip: true,
        cookie_flags: "SameSite=None;Secure"
      });
    }
  };

  const initMetaPixel = () => {
    // Meta Pixel (Facebook)
    // Replace PIXEL_ID with your actual Meta Pixel ID
    if (!window.fbq) {
      (function(f,b,e,v,n,t,s) {
        if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s);
      })(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
      
      window.fbq('init', 'PIXEL_ID');
      window.fbq('track', 'PageView');
    }
  };

  const initGoogleAds = () => {
    // Google Ads Conversion Tracking
    // Replace AW-XXXXXXXXX with your actual Google Ads ID
    if (!window.gtag) {
      const script = document.createElement("script");
      script.src = "https://www.googletagmanager.com/gtag/js?id=AW-XXXXXXXXX";
      script.async = true;
      document.head.appendChild(script);
      
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag("js", new Date());
    }
    window.gtag("config", "AW-XXXXXXXXX");
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    initializeScripts(prefs);
    setIsVisible(false);
    setShowDetails(false);
  };

  const togglePreference = (category) => {
    if (COOKIE_CATEGORIES[category].required) return;
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-overlay">
      <div className={`cookie-banner ${showDetails ? "cookie-banner-expanded" : ""}`}>
        <button 
          className="cookie-close" 
          onClick={handleRejectAll}
          aria-label="Chiudi e rifiuta cookie non essenziali"
        >
          <X size={20} />
        </button>
        
        <div className="cookie-header">
          <Cookie className="cookie-icon" size={32} />
          <div>
            <h3>Utilizziamo i cookie</h3>
            <p className="cookie-company">Isoliamo - Revogreen Srl | P.IVA 05121010233</p>
          </div>
        </div>

        <div className="cookie-content">
          <p>
            Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza, 
            analizzare il traffico e mostrarti pubblicità personalizzata su Meta (Facebook/Instagram) 
            e Google. Puoi accettare tutti i cookie o personalizzare le tue preferenze.
          </p>

          {showDetails && (
            <div className="cookie-details">
              {Object.values(COOKIE_CATEGORIES).map((category) => {
                const IconComponent = category.icon;
                return (
                  <div key={category.id} className="cookie-category">
                    <div className="category-header">
                      <div className="category-info">
                        <IconComponent size={20} className="category-icon" />
                        <div>
                          <h4>{category.name}</h4>
                          <p>{category.description}</p>
                        </div>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={preferences[category.id]}
                          onChange={() => togglePreference(category.id)}
                          disabled={category.required}
                        />
                        <span className={`toggle-slider ${category.required ? "toggle-required" : ""}`}></span>
                      </label>
                    </div>
                    {category.id === "marketing" && (
                      <div className="category-partners">
                        <span className="partner-badge">Meta Pixel</span>
                        <span className="partner-badge">Google Ads</span>
                      </div>
                    )}
                    {category.id === "analytics" && (
                      <div className="category-partners">
                        <span className="partner-badge">Google Analytics</span>
                      </div>
                    )}
                  </div>
                );
              })}
              
              <div className="cookie-legal">
                <p>
                  <strong>Titolare del trattamento:</strong> Revogreen Srl, Via Roma 44B, 37060 Mozzecane (VR)
                </p>
                <p>
                  <strong>Finalità:</strong> Funzionamento del sito, analisi del traffico, marketing e retargeting.
                </p>
                <p>
                  <strong>Base giuridica:</strong> Consenso dell'interessato (Art. 6.1.a GDPR).
                </p>
                <p>
                  <strong>Conservazione:</strong> I cookie di marketing sono conservati per max 13 mesi.
                </p>
                <p>
                  Puoi modificare le tue preferenze in qualsiasi momento. 
                  Per maggiori informazioni, consulta la nostra{" "}
                  <a href="#privacy" className="cookie-link">Privacy Policy</a> e la{" "}
                  <a href="#cookie" className="cookie-link">Cookie Policy</a>.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="cookie-actions">
          <Button
            variant="outline"
            onClick={() => setShowDetails(!showDetails)}
            className="cookie-btn-customize"
          >
            {showDetails ? "Nascondi dettagli" : "Personalizza"}
          </Button>
          
          {showDetails ? (
            <Button onClick={handleSavePreferences} className="cookie-btn-save">
              Salva preferenze
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleRejectAll}
                className="cookie-btn-reject"
              >
                Rifiuta non essenziali
              </Button>
              <Button onClick={handleAcceptAll} className="cookie-btn-accept">
                Accetta tutti
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CookieConsent;
